import React from "react";
import Icon from "components/AppIcon";

const SummaryCard = ({ data, formatCurrency, formatNumber }) => {
  if (!data) return null;

  // Helper function to format costs as positive values since they represent expenses
  const formatCost = (value) => {
    return formatCurrency(Math.abs(value));
  };

  const summaryMetrics = [
    {
      label: "Standard Process Total",
      value: Math.abs(data.standardProcess.projectTotal),
      icon: "Settings",
      color: "text-error",
      bgColor: "bg-error-50",
      borderColor: "border-error-200",
      type: "currency",
      isNegative: true,
    },
    {
      label: "Donation Model EBITA",
      value: data.looptFoundation.netEbitaContribution,
      icon: "Recycle",
      color: "text-text-primary",
      bgColor: "bg-success-50",
      borderColor: "border-success-200",
      type: "currency",
    },
    {
      label: "Total Revenue Potential",
      value: data.revenue.ragTraderRevenue + data.revenue.valueChannelRevenue,
      icon: "TrendingUp",
      color: "text-text-primary",
      bgColor: "bg-success-50",
      borderColor: "border-success-200",
      type: "currency",
    },
  ];

  const costComparison = {
    // Since we now display costs as positive, we need to adjust the comparison logic
    // Loopt EBITA is positive (contribution), Standard Process cost is positive (expense)
    difference:
      data.looptFoundation.netEbitaContribution +
      Math.abs(data.standardProcess.projectTotal),
    percentage:
      data.standardProcess.projectTotal !== 0
        ? ((data.looptFoundation.netEbitaContribution +
            Math.abs(data.standardProcess.projectTotal)) /
            Math.abs(data.standardProcess.projectTotal)) *
          100
        : 0,
  };

  return (
    <div className="bg-surface rounded-lg border border-border shadow-elevation-2 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-50 to-primary-100 px-6 py-4 border-b border-primary-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-heading-semibold text-primary mb-1">
              Calculation Summary
            </h2>
            <p className="text-sm text-primary-600">
              Key metrics for {data.inputs.material} processing
            </p>
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {summaryMetrics.map((metric, index) => (
            <div
              key={index}
              className={`${metric.bgColor} ${metric.borderColor} border rounded-lg p-4 transition-all duration-150 hover-scale`}
            >
              <div className="flex items-center justify-between mb-2">
                <div
                  className={`w-8 h-8 ${metric.bgColor} rounded-lg flex items-center justify-center`}
                >
                  <Icon name={metric.icon} size={16} className={metric.color} />
                </div>
                {metric.type === "currency" && (
                  <div className="text-xs text-text-tertiary">USD</div>
                )}
              </div>
              <div className="space-y-1">
                <div className={`text-2xl font-data ${metric.color}`}>
                  {metric.type === "currency"
                    ? metric.isNegative
                      ? `-${formatCurrency(metric.value)}`
                      : formatCurrency(metric.value)
                    : `${formatNumber(metric.value)}${
                        metric.suffix ? ` ${metric.suffix}` : ""
                      }`}
                </div>
                <div className="text-xs font-body-medium text-text-secondary">
                  {metric.label}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Cost Comparison Insight */}
        <div className="bg-surface-50 rounded-lg border border-border-light p-4">
          <div className="flex items-start space-x-3">
            <div
              className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                costComparison.difference > 0
                  ? "bg-warning-100"
                  : "bg-success-100"
              }`}
            >
              <Icon
                name={
                  costComparison.difference > 0 ? "TrendingUp" : "TrendingDown"
                }
                size={16}
                className={
                  costComparison.difference > 0
                    ? "text-warning"
                    : "text-success"
                }
              />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-body-medium text-text-primary mb-1">
                Processing Method Comparison
              </h3>
              <p className="text-sm text-text-secondary">
                Standard Process costs{" "}
                <span className="font-body-medium text-error">
                  {formatCost(data.standardProcess.projectTotal)}
                </span>{" "}
                vs Loopt Foundation EBITA contribution of{" "}
                <span className="font-body-medium text-success">
                  {formatCurrency(data.looptFoundation.netEbitaContribution)}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SummaryCard;
